import React, { useState, useEffect } from 'react';
import RoleDropdown from '../../../tabs/Dropdown/RoleDropdown';
import MedicalDropdown from '../../../tabs/Dropdown/MedicalDropdown';
import axiosInstance from '../../../../../api';

const AddUserModal = ({
  newUser,
  handleInputChange,
  handleAddUser,
  isEditMode,
  medicalCenters,
  isLoading,
  errors
}) => {
  const [localErrors, setLocalErrors] = useState({});
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    setLocalErrors(prev => ({ ...prev, ...errors }));
  }, [errors]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get('/ems/not-assigned-employee');
        const availableEmployees = response.data.data || response.data;
        setEmployees(availableEmployees);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const modal = document.getElementById('bs-user');
    const handleModalShow = () => {
      setLocalErrors({});
      if (newUser.role_name === 'EMS Resource Manager') {
        handleInputChange({ target: { name: 'medical_center_id', value: '0' } });
      }
      if (
        ['Hospital Representative (Nurse)', 'Hospital Representative (Doctor)', 'EMS Resource Manager'].includes(
          newUser.role_name
        ) && !newUser.medical_center_id
      ) {
        handleInputChange({ target: { name: 'medical_center_id', value: '0' } });
      }
      if (newUser.role_name === 'EMS Team' && !newUser.medical_center_id) {
        handleInputChange({ target: { name: 'medical_center_id', value: '0' } });
      }
    };
    modal.addEventListener('show.bs.modal', handleModalShow);
    return () => {
      modal.removeEventListener('show.bs.modal', handleModalShow);
    };
  }, [newUser.role_name, newUser.medical_center_id, handleInputChange]);

  const validateField = (name, value) => {
    let errorMsg = '';

    if (name === 'password') {
      if (!isEditMode && (!value || value.trim() === '')) {
        errorMsg = 'This field is required';
      }
    } else if (!value || value.trim() === '') {
      errorMsg = 'This field is required';
    }

    if (name === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errorMsg = 'Invalid email format';
    }
    if (name === 'phone' && value && !/^\d{10}$/.test(value)) {
      errorMsg = 'Phone number must be exactly 10 digits';
    }
    if (name === 'emp_id' && newUser.role_name === 'EMS Team' && !value) {
      errorMsg = 'Please select an employee';
    }

    setLocalErrors(prevErrors => ({ ...prevErrors, [name]: errorMsg }));
    return errorMsg === '';
  };

  const handleInputChangeWithValidation = e => {
    const { name, value } = e.target;
    if (localErrors[name]) {
      validateField(name, value);
    }
    handleInputChange(e);
  };

  const handleSubmit = e => {
    e.preventDefault();
    let isValid = true;

    try {
      const fieldsToValidate = [
        'first_name',
        'last_name',
        'username',
        'phone',
        'email',
        'password',
        'address',
        'national_id',
        'dob'
      ];

      fieldsToValidate.forEach(field => {
        const valid = validateField(field, newUser[field] || '');
        if (!valid) {
          isValid = false;
        }
      });

      if (!newUser.role_id) {
        setLocalErrors(prev => ({ ...prev, role_id: 'Please select a role' }));
        isValid = false;
      }

      if (newUser.role_name === 'EMS Team' && !newUser.emp_id) {
        setLocalErrors(prev => ({
          ...prev,
          emp_id: 'Please select an employee'
        }));
        isValid = false;
      }

      const requiredMedicalRoles = [
        'Hospital Representative (Nurse)',
        'Hospital Representative (Doctor)'
      ];

      if (newUser.role_name === 'EMS Resource Manager') {
        handleInputChange({ target: { name: 'medical_center_id', value: newUser.medical_center_id || '0' } });
      } else if (requiredMedicalRoles.includes(newUser.role_name)) {
        if (!newUser.medical_center_id || newUser.medical_center_id === '0' || newUser.medical_center_id === 0) {
          setLocalErrors(prev => ({ ...prev, medical_center_id: 'Please select a medical center' }));
          isValid = false;
        }
      } else {
        handleInputChange({ target: { name: 'medical_center_id', value: '0' } });
      }

      if (isValid) {
        handleAddUser();
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  };

  const handleRoleChange = e => {
    const selectedRoleId = parseInt(e.target.value, 10);
    const selectedRoleName = e.target.options[e.target.selectedIndex].text;

    handleInputChange({ target: { name: 'role_id', value: selectedRoleId } });
    handleInputChange({ target: { name: 'role_name', value: selectedRoleName } });

    const requiredMedicalRoles = [
      'Hospital Representative (Nurse)',
      'Hospital Representative (Doctor)'
    ];

    if (selectedRoleName === 'EMS Resource Manager') {
      handleInputChange({ target: { name: 'medical_center_id', value: newUser.medical_center_id || '0' } });
    } else if (!requiredMedicalRoles.includes(selectedRoleName)) {
      handleInputChange({ target: { name: 'medical_center_id', value: '0' } });
    }

    if (selectedRoleName !== 'EMS Team') {
      handleInputChange({ target: { name: 'emp_id', value: '' } });
    }

    setLocalErrors(prevErrors => ({ ...prevErrors, role_id: '' }));
  };

  const handleEmployeeChange = e => {
    const selectedEmpId = e.target.value;
    handleInputChange({ target: { name: 'emp_id', value: selectedEmpId } });
    setLocalErrors(prev => ({ ...prev, emp_id: '' }));
  };

  const enableMedicalCenter = ['Hospital Representative (Nurse)', 'Hospital Representative (Doctor)', 'EMS Resource Manager'].includes(
    newUser.role_name
  );
  const showEmployeeDropdown = newUser.role_name === 'EMS Team';

  return (
    <div id="bs-user" className="modal fade" tabIndex="-1" aria-labelledby="bs-user" aria-hidden="true">
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">{isEditMode ? 'Edit User' : 'Add User'}</h5>
            <button
              id="closeAddUserModal"
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {['first_name', 'last_name', 'username', 'phone', 'email', 'password'].map(field => (
                  <div key={field} className="col-md-6 mb-3">
                    <label className="form-label">
                      {field.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} <span className="text-danger">*</span>
                    </label>
                    <input
                      type={
                        field === 'email'
                          ? 'email'
                          : field === 'password'
                            ? 'password'
                            : field === 'phone'
                              ? 'tel'
                              : 'text'
                      }
                      name={field}
                      value={newUser[field] || ''}
                      onChange={handleInputChangeWithValidation}
                      className={`form-control ${localErrors[field] ? 'is-invalid' : ''}`}
                      placeholder={`Enter ${field.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
                      disabled={isLoading && field !== 'password'}
                      maxLength={field === 'phone' ? 10 : undefined}
                    />
                    {localErrors[field] && <div className="invalid-feedback">{localErrors[field]}</div>}
                  </div>
                ))}

                <div className="col-md-6 mb-3">
                  <label className="form-label">National ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="national_id"
                    value={newUser.national_id || ''}
                    onChange={handleInputChangeWithValidation}
                    className={`form-control ${localErrors.national_id ? 'is-invalid' : ''}`}
                    placeholder="Enter National ID"
                    disabled={isLoading}
                  />
                  {localErrors.national_id && (
                    <div className="invalid-feedback">{localErrors.national_id}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    name="dob"
                    value={newUser.dob || ''}
                    onChange={handleInputChangeWithValidation}
                    className={`form-control ${localErrors.dob ? 'is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {localErrors.dob && (
                    <div className="invalid-feedback">{localErrors.dob}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Role <span className="text-danger">*</span></label>
                  <RoleDropdown
                    value={newUser.role_id}
                    onChange={handleRoleChange}
                    disabled={isLoading}
                    className={localErrors.role_id ? 'is-invalid' : ''}
                  />
                  {localErrors.role_id && (
                    <div className="invalid-feedback d-block">{localErrors.role_id}</div>
                  )}
                </div>

                {!showEmployeeDropdown && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Medical Center </label>
                    <MedicalDropdown
                      value={newUser.medical_center_id || '0'}
                      onChange={e => {
                        const val = e.target.value;
                        handleInputChange({
                          target: {
                            name: 'medical_center_id',
                            value: val === 'ALL' ? '0' : val
                          }
                        });
                        setLocalErrors(prev => ({ ...prev, medical_center_id: '' }));
                      }}
                      disabled={!enableMedicalCenter || isLoading}
                      medicalCenters={medicalCenters}
                      roleName={newUser.role_name}
                      showAllOption={newUser.role_name === 'EMS Resource Manager'}
                    />
                    {localErrors.medical_center_id && (
                      <div className="invalid-feedback d-block">{localErrors.medical_center_id}</div>
                    )}
                  </div>
                )}

                {showEmployeeDropdown && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Employee <span className="text-danger">*</span> <i className="bi bi-person-badge ms-1"></i>
                    </label>
                    <select
                      name="emp_id"
                      value={newUser.emp_id || ''}
                      onChange={handleEmployeeChange}
                      className={`form-control ${localErrors.emp_id ? 'is-invalid' : ''}`}
                      disabled={isLoading}
                    >
                      <option value="">Select Employee</option>
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                        </option>
                      ))}
                    </select>
                    {localErrors.emp_id && (
                      <div className="invalid-feedback">{localErrors.emp_id}</div>
                    )}
                  </div>
                )}

                <div className="col-md-12 mb-3">
                  <label className="form-label">Address <span className="text-danger">*</span></label>
                  <textarea
                    name="address"
                    value={newUser.address || ''}
                    onChange={handleInputChangeWithValidation}
                    className={`form-control ${localErrors.address ? 'is-invalid' : ''}`}
                    rows="3"
                    placeholder="Enter Address"
                    disabled={isLoading}
                  ></textarea>
                  {localErrors.address && (
                    <div className="invalid-feedback">{localErrors.address}</div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  data-bs-dismiss="modal"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || Object.values(localErrors).some(error => error !== '')}
                >
                  {isLoading && (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {isEditMode ? 'Update User' : 'Save User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
